// Preset suggested options for Sprint steps 1-3.
// Pure data + helpers. No DB, no AI, no network calls.

import type { V2RealityCheck } from "@/v2/hooks/useV2Sprint";

export interface SuggestedItem {
  id: string;
  label: string;
  description?: string;
}

export interface HypothesisSuggestion {
  id: string;
  /** Etichetta breve della card (es: "Urgenza del problema"). */
  title: string;
  /** Ipotesi falsificabile completa: target + azione misurabile + numero + tempo. */
  hypothesis: string;
  /** Perché questa ipotesi è rischiosa / vale la pena testarla. */
  why: string;
  /** True se è un fallback non-falsificabile (es: main_risk grezzo). */
  isRaw?: boolean;
}

export interface ExperimentPreset extends SuggestedItem {
  id:
    | "landing_waitlist"
    | "interviste"
    | "fake_door"
    | "smoke_test"
    | "concierge_test";
  defaultThreshold: string;
  /** Checklist operativa di preparazione/lancio del test (usata in Step 3). */
  executionChecklist: string[];
  /** Metriche grezze da raccogliere durante il test (Step 3 mostra, Step 4 raccoglie). */
  executionMetrics: string[];
  /** Cosa salvare come evidenza concreta (screenshot, file, trascrizioni). */
  evidenceItems: string[];
}

export const EXPERIMENT_PRESETS: ExperimentPreset[] = [
  {
    id: "landing_waitlist",
    label: "Landing page / lista d'attesa",
    description:
      "Pagina con value prop chiara + form email per misurare interesse reale. Il traffico può venire da ads, post o community.",
    defaultThreshold: "≥10 email qualificate in 7 giorni",
    executionChecklist: [
      "Scrivi headline + value proposition testabile",
      "Crea landing minima (Carrd / Framer / Lovable)",
      "Aggiungi form email / lista d'attesa visibile",
      "Pubblica la landing e annota URL",
      "Porta traffico (post organici, DM, community, eventuali ads)",
      "Registra visite, click sulla CTA ed email raccolte",
    ],
    executionMetrics: [
      "Visite totali alla landing",
      "Click sulla CTA principale",
      "Email lasciate / iscrizioni alla waitlist",
      "Sorgenti di traffico principali",
    ],
    evidenceItems: [
      "URL pubblico della landing",
      "Screenshot di analytics (visite, sorgenti)",
      "Export o screenshot della lista email raccolte",
      "Screenshot dei post / DM usati per portare traffico",
    ],
  },
  {
    id: "interviste",
    label: "Interviste problema/soluzione",
    description:
      "5-10 conversazioni 1:1 con il target per validare problema e disponibilità a pagare.",
    defaultThreshold: "≥5 interviste con problema riconosciuto spontaneamente",
    executionChecklist: [
      "Definisci profilo intervistato (1 frase precisa)",
      "Prepara lista di 20+ persone target da contattare",
      "Scrivi messaggio di invito breve (DM / email)",
      "Scrivi 5 domande aperte, non leading",
      "Contatta e fissa le call",
      "Conduci le interviste e prendi note testuali",
      "Registra risposte, citazioni e segnali",
    ],
    executionMetrics: [
      "Persone contattate",
      "Risposte ricevute",
      "Call fissate",
      "Interviste effettivamente fatte",
      "Quante hanno riconosciuto il problema spontaneamente",
    ],
    evidenceItems: [
      "Trascrizioni o note delle interviste",
      "Citazioni testuali (non parafrasate)",
      "Eventuali registrazioni (con consenso esplicito)",
      "Screenshot dei messaggi di invito e risposte",
    ],
  },
  {
    id: "fake_door",
    label: "Fake door",
    description:
      "Mostra una feature/prodotto come se esistesse e misura quanti cliccano davvero.",
    defaultThreshold: "≥3% click-through sulla CTA principale",
    executionChecklist: [
      "Scrivi promessa / CTA della fake door",
      "Crea bottone o pagina fake door dove c'è già traffico",
      "Prepara messaggio post-click (\"in arrivo\" + form email)",
      "Pubblica e tieni attivo almeno 7 giorni",
      "Porta traffico se necessario",
      "Registra impression, click e email lasciate",
    ],
    executionMetrics: [
      "Impression della CTA",
      "Click sulla CTA",
      "Click-through rate",
      "Email lasciate dopo il click",
    ],
    evidenceItems: [
      "Screenshot della CTA e della pagina post-click",
      "Screenshot di analytics (impression, click)",
      "Export delle email raccolte",
    ],
  },
  {
    id: "smoke_test",
    label: "Smoke test",
    description:
      "Annuncio pubblico (post, video, email) per testare reazione e raccogliere segnali.",
    defaultThreshold: "≥20 reazioni rilevanti o ≥10 risposte qualificate",
    executionChecklist: [
      "Scrivi annuncio breve (1 problema, 1 promessa)",
      "Scegli 2-3 canali dove c'è il target",
      "Pubblica e annota link/permalink",
      "Rispondi a chi commenta o scrive entro 24h",
      "Registra reazioni, commenti, DM e iscrizioni",
    ],
    executionMetrics: [
      "Impression / visualizzazioni",
      "Reazioni (like, save, ecc.)",
      "Commenti rilevanti",
      "DM o risposte ricevute",
      "Iscrizioni / contatti qualificati",
    ],
    evidenceItems: [
      "Link pubblico ai post",
      "Screenshot di reazioni, commenti, DM",
      "Lista o screenshot dei contatti raccolti",
    ],
  },
  {
    id: "concierge_test",
    label: "Concierge / test manuale",
    description:
      "Eroghi il servizio a mano, senza prodotto, per validare valore percepito prima di costruire.",
    defaultThreshold: "≥3 utenti che completano il flusso e tornano",
    executionChecklist: [
      "Definisci la mini-offerta concierge (cosa fai, in cambio di cosa)",
      "Identifica 3-5 utenti disposti a provare",
      "Scrivi messaggio di proposta",
      "Eroga il servizio manualmente (chat, email, call)",
      "Osserva cosa chiedono, cosa salta, cosa li fa tornare",
      "Registra completamento, ripetizione e disponibilità a pagare",
    ],
    executionMetrics: [
      "Utenti contattati",
      "Prove accettate",
      "Prove completate",
      "Ritorni / richieste successive",
      "Disponibilità a pagare espressa",
    ],
    evidenceItems: [
      "Screenshot delle conversazioni di erogazione",
      "Note qualitative su cosa chiedono e cosa salta",
      "Eventuali pagamenti, preordini o impegni espliciti",
    ],
  },
];

export function buildHypothesisSuggestions(
  rc: V2RealityCheck | null,
): HypothesisSuggestion[] {
  // Se non c'è RC, usa i template generici invariati
  if (!rc) {
    return [
      {
        id: "h_urgency",
        title: "Urgenza del problema",
        hypothesis:
          "Almeno 4 persone su 10 intervistate, quando descrivono il problema, citano un tentativo di soluzione attivo negli ultimi 30 giorni.",
        why: "Se non hanno già provato a risolverlo da soli, il problema non è abbastanza urgente per pagare ora.",
      },
      {
        id: "h_willingness",
        title: "Disponibilità a pagare",
        hypothesis:
          "Almeno il 5% di chi visita la landing lascia email + carta per un preordine a 49€ entro 7 giorni.",
        why: "Interesse e click non sono pagamento. Una conversione monetaria reale separa curiosi da clienti.",
      },
      {
        id: "h_channel",
        title: "Canale sostenibile",
        hypothesis:
          "Con 30€ di ads o 50 DM manuali ottengo almeno 10 lead qualificati in 7 giorni.",
        why: "Se il costo per lead è troppo alto, l'idea non scala anche se il prodotto funziona.",
      },
    ];
  }

  // Personalizza usando i dati RC
  const target = rc.target_summary?.trim() || "persone del target";

  // Canale: mappa test_channel a testo leggibile
  const channelMap: Record<string, string> = {
    linkedin: "LinkedIn DM",
    email: "email diretta",
    phone: "chiamate",
    community: "community online",
    instagram_dm: "Instagram DM",
    whatsapp: "WhatsApp",
    cold_walk_in: "contatti diretti",
    other: "DM manuali",
    unknown: "DM manuali",
  };
  const channelLabel =
    channelMap[(rc as any).test_channel as string] ?? "DM manuali";

  const list: HypothesisSuggestion[] = [
    {
      id: "h_urgency",
      title: "Urgenza del problema",
      hypothesis: `Almeno 4 ${target} su 10 intervistati, quando descrivono il problema, citano un tentativo di soluzione attivo negli ultimi 30 giorni.`,
      why: "Se non hanno già provato a risolverlo da soli, il problema non è abbastanza urgente per pagare ora.",
    },
    {
      id: "h_willingness",
      title: "Disponibilità a pagare",
      hypothesis: `Almeno il 5% di ${target} che vedono la proposta lascia un contatto reale (email o richiesta demo) entro 7 giorni.`,
      why: "Interesse e click non sono validazione. Un contatto reale separa curiosi da potenziali clienti.",
    },
    {
      id: "h_channel",
      title: "Canale sostenibile",
      hypothesis: `Con 50 messaggi via ${channelLabel} ottengo almeno 10 risposte interessate da ${target} in 7 giorni.`,
      why: "Se il canale non risponde, l'idea non scala anche se il prodotto funziona.",
    },
  ];

  // Se c'è un main_risk significativo, aggiungilo come ipotesi derivata dal RC
  const rawRisk = rc.main_risk?.trim();
  if (rawRisk && rawRisk.length > 20) {
    list.push({
      id: "h_rc_risk",
      title: "Rischio principale (dal tuo RC)",
      hypothesis: rc.minimum_success_threshold?.trim() || rawRisk,
      why: rawRisk,
      isRaw: !rc.minimum_success_threshold?.trim(),
    });
  }

  return list;
}

export function guessExperimentFromRC(
  rc: V2RealityCheck | null,
): ExperimentPreset["id"] | null {
  if (!rc?.experiment_48h) return null;
  const t = rc.experiment_48h.toLowerCase();
  if (/(intervist|call|conversazion)/.test(t)) return "interviste";
  if (/(fake door|finta porta)/.test(t)) return "fake_door";
  if (/(smoke|annuncio|post)/.test(t)) return "smoke_test";
  if (/(concierge|manual)/.test(t)) return "concierge_test";
  if (/(landing|waitlist|lista d'attesa|ads|adv|annunci)/.test(t))
    return "landing_waitlist";
  return null;
}

export function getExperimentPreset(
  id: string | undefined | null,
): ExperimentPreset | null {
  if (!id) return null;
  // Back-compat: legacy "landing_ads" id maps to the renamed preset
  const normalized = id === "landing_ads" ? "landing_waitlist" : id;
  return EXPERIMENT_PRESETS.find((e) => e.id === normalized) ?? null;
}

/** Checklist suggestions used by Step 3 (preparation + launch actions). */
export function buildChecklistSuggestions(
  selectedExperimentId: string | undefined | null,
): SuggestedItem[] {
  const preset = getExperimentPreset(selectedExperimentId);
  if (!preset) return [];
  return preset.executionChecklist.map((label, i) => ({
    id: `c_${preset.id}_${i}`,
    label,
  }));
}

/** Read-only metrics list Step 3 shows as the "data contract" for Step 4. */
export function getExecutionMetrics(
  selectedExperimentId: string | undefined | null,
): string[] {
  const preset = getExperimentPreset(selectedExperimentId);
  return preset?.executionMetrics ?? [];
}

/** Read-only evidence list Step 3 shows ("cosa salvare come evidenza"). */
export function getEvidenceItems(
  selectedExperimentId: string | undefined | null,
): string[] {
  const preset = getExperimentPreset(selectedExperimentId);
  return preset?.evidenceItems ?? [];
}

// ---------------------------------------------------------------------------
// Step 3 operational workspace — execution plan, metrics & evidence trackers
// ---------------------------------------------------------------------------

/** Deterministic per-experiment split into preparation / execution phases.
 *  IDs are stable so checks persist across renders, refetch and unrelated
 *  state changes. User-added items get a fresh UUID instead. */
const EXECUTION_PLAN_SPLITS: Record<
  ExperimentPreset["id"],
  { preparation: string[]; execution: string[] }
> = {
  landing_waitlist: {
    preparation: [
      "Scrivi headline + value proposition testabile",
      "Crea landing minima (Carrd / Framer / Lovable)",
      "Aggiungi form email / lista d'attesa visibile",
      "Pubblica la landing e annota URL",
    ],
    execution: [
      "Porta traffico (post organici, DM, community, eventuali ads)",
      "Registra visite, click sulla CTA ed email raccolte",
      "Salva screenshot di analytics ed eventuali export",
    ],
  },
  interviste: {
    preparation: [
      "Definisci profilo intervistato (1 frase precisa)",
      "Prepara lista di 20+ persone target da contattare",
      "Scrivi messaggio di invito breve (DM / email)",
      "Scrivi 5 domande aperte, non leading",
    ],
    execution: [
      "Contatta i target e fissa le call",
      "Conduci le interviste e prendi note testuali",
      "Registra risposte, citazioni e segnali",
      "Esegui follow-up con chi non ha risposto",
    ],
  },
  fake_door: {
    preparation: [
      "Scrivi promessa / CTA della fake door",
      "Crea bottone o pagina fake door dove c'è già traffico",
      "Prepara messaggio post-click (\"in arrivo\" + form email)",
    ],
    execution: [
      "Pubblica e tieni attivo almeno 7 giorni",
      "Porta traffico se necessario",
      "Registra impression, click ed email lasciate",
    ],
  },
  smoke_test: {
    preparation: [
      "Scrivi annuncio breve (1 problema, 1 promessa)",
      "Scegli 2-3 canali dove c'è il target",
    ],
    execution: [
      "Pubblica e annota link/permalink",
      "Rispondi a chi commenta o scrive entro 24h",
      "Registra reazioni, commenti, DM e iscrizioni",
    ],
  },
  concierge_test: {
    preparation: [
      "Definisci la mini-offerta concierge (cosa fai, in cambio di cosa)",
      "Identifica 3-5 utenti disposti a provare",
      "Scrivi messaggio di proposta",
    ],
    execution: [
      "Eroga il servizio manualmente (chat, email, call)",
      "Osserva cosa chiedono, cosa salta, cosa li fa tornare",
      "Registra completamento, ripetizione e disponibilità a pagare",
    ],
  },
};

export function buildExecutionPlan(
  selectedExperimentId: string | undefined | null,
): { preparation: SuggestedItem[]; execution: SuggestedItem[] } {
  const preset = getExperimentPreset(selectedExperimentId);
  if (!preset) return { preparation: [], execution: [] };
  const split = EXECUTION_PLAN_SPLITS[preset.id] ?? {
    preparation: [],
    execution: preset.executionChecklist,
  };
  return {
    preparation: split.preparation.map((label, i) => ({
      id: `prep_${preset.id}_${i}`,
      label,
    })),
    execution: split.execution.map((label, i) => ({
      id: `exec_${preset.id}_${i}`,
      label,
    })),
  };
}

export function buildMetricsToCollect(
  selectedExperimentId: string | undefined | null,
): SuggestedItem[] {
  const preset = getExperimentPreset(selectedExperimentId);
  if (!preset) return [];
  return preset.executionMetrics.map((label, i) => ({
    id: `metric_${preset.id}_${i}`,
    label,
  }));
}

export function buildEvidenceToCollect(
  selectedExperimentId: string | undefined | null,
): SuggestedItem[] {
  const preset = getExperimentPreset(selectedExperimentId);
  if (!preset) return [];
  return preset.evidenceItems.map((label, i) => ({
    id: `evidence_${preset.id}_${i}`,
    label,
  }));
}

// ---------------------------------------------------------------------------
// M6.1 — Seed metadata, alias normalization, universal fallback,
//        deterministic personalization, "Cosa fare adesso" queue.
// ---------------------------------------------------------------------------

export type ChecklistSeedSource = "preset" | "fallback" | "legacy";

export interface Step3SeedMetadata {
  checklist_initialized_for_experiment?: string | null;
  checklist_user_modified?: boolean;
  checklist_seed_source?: ChecklistSeedSource;
}

export interface ExperimentSnapshotLike {
  top_hypothesis?: string;
  experiment_type?: string;
  experiment_label?: string;
  target?: string;
  channel?: string;
  action?: string;
  primary_metric?: string;
  success_threshold?: string;
  duration_days?: number | string | null;
  minimum_sample?: number | string | null;
}

/** Maps any user/legacy experiment value to the 5 known presets, or "custom". */
export function normalizeExperimentId(value?: string | null): string {
  if (!value || typeof value !== "string") return "custom";
  const k = value.trim().toLowerCase().replace(/[-\s]+/g, "_");
  if (!k) return "custom";
  if (["interviste", "interview", "interviews", "discovery_interviews"].includes(k))
    return "interviste";
  if (
    [
      "landing_waitlist",
      "landing",
      "landing_page",
      "landing_ads",
      "waitlist",
    ].includes(k)
  )
    return "landing_waitlist";
  if (["fake_door", "fakedoor"].includes(k)) return "fake_door";
  if (["smoke", "smoke_test"].includes(k)) return "smoke_test";
  if (["concierge", "concierge_test", "manual", "manual_test"].includes(k))
    return "concierge_test";
  return "custom";
}

/** Extracts the first positive integer from free-text minimum_sample. */
export function parseMinimumSample(
  raw: number | string | null | undefined,
): number | null {
  if (typeof raw === "number" && Number.isFinite(raw) && raw > 0) {
    return Math.floor(raw);
  }
  if (typeof raw !== "string") return null;
  const m = raw.match(/\d+/);
  if (!m) return null;
  const n = parseInt(m[0], 10);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

/** Universal operational plan when the experiment has no preset (custom/legacy). */
export function buildUniversalExecutionPlan(): {
  preparation: SuggestedItem[];
  execution: SuggestedItem[];
} {
  const preparation = [
    "Definisci una lista concreta di persone o aziende del target da raggiungere.",
    "Prepara il messaggio, la pagina o la modalità con cui presenterai il test.",
    "Prepara un sistema semplice per registrare contatti, risposte e azioni.",
    "Verifica quale comportamento vuoi osservare e quale soglia userai.",
    "Prepara il canale scelto per eseguire il test.",
  ].map((label, i) => ({ id: `prep_custom_${i}`, label }));
  const execution = [
    "Esegui il primo invio, pubblicazione o contatto.",
    "Registra ogni risposta o comportamento osservabile.",
    "Esegui almeno un follow-up.",
    "Salva screenshot, citazioni, link o altre prove.",
    "Confronta i risultati raccolti con la soglia minima.",
    "Torna nello Sprint e aggiorna metriche ed evidenze.",
  ].map((label, i) => ({ id: `exec_custom_${i}`, label }));
  return { preparation, execution };
}

// ---- Personalization ------------------------------------------------------

function clean(s: string | null | undefined): string {
  if (typeof s !== "string") return "";
  return s.trim().replace(/\s+/g, " ");
}

/** Applies deterministic, grammar-safe substitutions to plan items using
 *  snapshot fields. Unknown items pass through unchanged. Never produces
 *  "NaN", double units, or broken articles. */
export function personalizeExecutionPlan(
  plan: { preparation: SuggestedItem[]; execution: SuggestedItem[] },
  snapshot: ExperimentSnapshotLike | null | undefined,
): { preparation: SuggestedItem[]; execution: SuggestedItem[] } {
  const target = clean(snapshot?.target);
  const channel = clean(snapshot?.channel);
  const threshold = clean(snapshot?.success_threshold);
  const sampleN = parseMinimumSample(snapshot?.minimum_sample);

  const targetTxt = target || "persone del target";
  const channelTxt = channel ? `tramite ${channel}` : "attraverso il canale scelto";
  const groupTxt = sampleN
    ? `${sampleN} ${targetTxt}`
    : target
      ? `un primo gruppo di ${targetTxt}`
      : "un primo gruppo di persone del target";
  const firstBatch = sampleN ? Math.min(10, sampleN) : null;
  const firstBatchTxt = firstBatch
    ? `alle prime ${firstBatch} ${targetTxt}`
    : target
      ? `a un primo gruppo di ${targetTxt}`
      : "a un primo gruppo di persone del target";
  const thresholdSuffix = threshold
    ? ` Confronta con la soglia: ${threshold}.`
    : " Confronta con la soglia definita nello Step 2.";

  const rewriteOne = (item: SuggestedItem): SuggestedItem => {
    const l = item.label;
    const low = l.toLowerCase();

    // Preparation patterns
    if (/lista di 20\+/.test(low)) {
      return { ...item, label: `Prepara una lista di ${groupTxt} da contattare.` };
    }
    if (/identifica 3-5 utenti/.test(low)) {
      const n = sampleN ? Math.min(5, sampleN) : 5;
      return {
        ...item,
        label: target
          ? `Identifica ${n} ${targetTxt} disposti a provare.`
          : `Identifica ${n} utenti del target disposti a provare.`,
      };
    }
    if (/scegli 2-3 canali/.test(low) && channel) {
      return { ...item, label: `Pubblica sul canale scelto: ${channel}.` };
    }
    if (
      (/scrivi messaggio di invito/.test(low) ||
        /scrivi messaggio di proposta/.test(low)) &&
      channel
    ) {
      return {
        ...item,
        label: `${l.replace(/\.$/, "")} da inviare ${channelTxt}.`,
      };
    }
    if (/^prepara il canale/.test(low) && channel) {
      return { ...item, label: `Prepara il canale scelto: ${channel}.` };
    }

    // Execution patterns
    if (
      /porta traffico/.test(low) ||
      /^pubblica e annota/.test(low) ||
      /^pubblica e tieni attivo/.test(low)
    ) {
      return channel
        ? { ...item, label: `${l.replace(/\.$/, "")} ${channelTxt}.` }
        : item;
    }
    if (/contatta i target/.test(low) || /contatta e fissa/.test(low)) {
      return {
        ...item,
        label: `Contatta ${firstBatchTxt} ${channelTxt} e fissa le call.`,
      };
    }
    if (/^esegui il primo invio/.test(low)) {
      return {
        ...item,
        label: `Invia il primo contatto ${firstBatchTxt} ${channelTxt}.`,
      };
    }
    if (/^esegui almeno un follow-up/.test(low) && channel) {
      return { ...item, label: `Esegui almeno un follow-up ${channelTxt}.` };
    }
    if (/^confronta i risultati/.test(low)) {
      return {
        ...item,
        label: threshold
          ? `Confronta i risultati raccolti con la soglia: ${threshold}.`
          : `Confronta i risultati raccolti con la soglia definita nello Step 2.`,
      };
    }
    if (
      /^registra/.test(low) &&
      /(risposte|reazioni|contatti|email|click|commenti)/.test(low)
    ) {
      return { ...item, label: `${l.replace(/\.$/, "")}.${thresholdSuffix}` };
    }
    if (/^salva screenshot/.test(low) && channel) {
      return {
        ...item,
        label: `Salva screenshot delle conversazioni e dei risultati ottenuti ${channelTxt}.`,
      };
    }
    return item;
  };

  return {
    preparation: plan.preparation.map(rewriteOne),
    execution: plan.execution.map(rewriteOne),
  };
}

// ---- Next-action queue ----------------------------------------------------

export type NextActionCategory = "prep" | "live" | "measure" | "evidence";
export interface NextAction {
  id: string;
  label: string;
  category: NextActionCategory;
  sourceId: string;
}

interface ChecklistItemLike {
  id: string;
  label: string;
  checked: boolean;
}

export function getNextActions(args: {
  preparationChecklist: ChecklistItemLike[];
  executionChecklist: ChecklistItemLike[];
  metricsToCollect: ChecklistItemLike[];
  evidenceToCollect: ChecklistItemLike[];
}): NextAction[] {
  const out: NextAction[] = [];
  const push = (
    items: ChecklistItemLike[],
    category: NextActionCategory,
    labelFn: (it: ChecklistItemLike) => string,
  ) => {
    for (const it of items) {
      if (out.length >= 3) return;
      if (it.checked) continue;
      out.push({
        id: `next_${it.id}`,
        sourceId: it.id,
        category,
        label: labelFn(it),
      });
    }
  };
  push(args.preparationChecklist, "prep", (it) => it.label || "Voce senza titolo");
  push(args.executionChecklist, "live", (it) => it.label || "Voce senza titolo");
  push(args.metricsToCollect, "measure", (it) =>
    `Raccogli un valore reale per: ${it.label || "metrica"}`,
  );
  push(args.evidenceToCollect, "evidence", (it) =>
    `Salva questa prova: ${it.label || "evidenza"}`,
  );
  return out;
}

// ===========================================================================
// M6.2 — Step 4 Value Upgrade: dynamic metrics, threshold parsing,
//        structured qualitative evidence, evidence quality, legacy mirrors.
// ===========================================================================

export type MetricUnit = "count" | "percentage" | "currency" | "duration" | "other";
export type MetricSource = "step3" | "preset" | "fallback" | "custom";

export interface MetricResult {
  id: string;
  label: string;
  value: number | null;
  unit: MetricUnit;
  source: MetricSource;
  available_from_step3?: boolean;
  is_primary?: boolean;
  /** Stable semantic tag used by legacy mirror + threshold mapping. */
  semantic?: MetricSemantic;
}

/** Stable semantic tags so threshold mapping & legacy mirrors don't depend
 *  on free-text labels. */
export type MetricSemantic =
  | "reached"        // visite / impression / contattati / invitati
  | "responses"      // click / risposte
  | "conversations"  // conversazioni complete / accettate
  | "qualified"      // problema confermato / completate / lead qualificati
  | "commitments"    // micro-impegni / iscrizioni / pre-order / disponibili a pagare
  | "primary_custom" // fallback "Risultato principale"
  | "other";

export interface ThresholdAssessment {
  source_text: string;
  mapped_metric_id: string | null;
  operator: "gte" | "gt" | "lte" | "lt" | "equal" | null;
  target_value: number | null;
  denominator_metric_id?: string | null;
  denominator_target?: number | null;
  timeframe_text?: string | null;
  parse_confidence: "high" | "low";
  manually_confirmed: boolean;
}

export interface QualitativeEvidence {
  id: string;
  quote: string;
  source: string;
  sentiment: "positive" | "neutral" | "negative";
  link?: string;
}

export type TypedEvidenceLinkType =
  | "screenshot"
  | "analytics"
  | "post"
  | "landing"
  | "document"
  | "video"
  | "other";

export interface TypedEvidenceLink {
  id: string;
  url: string;
  label: string;
  type: TypedEvidenceLinkType;
}

export interface EvidenceQualityAssessment {
  level: "weak" | "sufficient" | "strong";
  checks: {
    primary_metric_present: boolean;
    sample_sufficient: boolean | null;
    threshold_comparable: boolean;
    qualitative_evidence_present: boolean;
    supporting_link_present: boolean;
  };
}

// ----- Metric definitions per experiment ----------------------------------

interface MetricDef {
  key: string;
  label: string;
  unit: MetricUnit;
  semantic: MetricSemantic;
  is_primary?: boolean;
}

const STEP4_METRIC_DEFS: Record<string, MetricDef[]> = {
  interviste: [
    { key: "contacted", label: "Persone contattate", unit: "count", semantic: "reached" },
    { key: "responses", label: "Risposte ricevute", unit: "count", semantic: "responses" },
    { key: "conversations", label: "Conversazioni completate", unit: "count", semantic: "conversations" },
    { key: "problem_confirmed", label: "Hanno confermato il problema", unit: "count", semantic: "qualified", is_primary: true },
    { key: "commitments", label: "Micro-impegni ottenuti", unit: "count", semantic: "commitments" },
  ],
  landing_waitlist: [
    { key: "visits", label: "Visite alla landing", unit: "count", semantic: "reached" },
    { key: "cta_clicks", label: "Click sulla CTA", unit: "count", semantic: "responses" },
    { key: "signups", label: "Email / iscrizioni raccolte", unit: "count", semantic: "commitments", is_primary: true },
    { key: "deep_requests", label: "Richieste di approfondimento", unit: "count", semantic: "qualified" },
  ],
  fake_door: [
    { key: "impressions", label: "Visualizzazioni / impression", unit: "count", semantic: "reached" },
    { key: "cta_clicks", label: "Click sulla CTA", unit: "count", semantic: "responses", is_primary: true },
    { key: "post_click_actions", label: "Azioni successive al click", unit: "count", semantic: "qualified" },
    { key: "contacts", label: "Contatti raccolti", unit: "count", semantic: "commitments" },
  ],
  smoke_test: [
    { key: "impressions", label: "Impression / visualizzazioni", unit: "count", semantic: "reached" },
    { key: "clicks", label: "Click / interazioni", unit: "count", semantic: "responses" },
    { key: "replies", label: "Risposte e DM ricevute", unit: "count", semantic: "conversations" },
    { key: "leads", label: "Lead / iscrizioni qualificate", unit: "count", semantic: "commitments", is_primary: true },
    { key: "demo_requests", label: "Richieste demo o info", unit: "count", semantic: "qualified" },
  ],
  concierge_test: [
    { key: "invited", label: "Persone invitate", unit: "count", semantic: "reached" },
    { key: "accepted", label: "Hanno accettato di provare", unit: "count", semantic: "responses" },
    { key: "completed", label: "Hanno completato la prova", unit: "count", semantic: "qualified", is_primary: true },
    { key: "continued", label: "Hanno chiesto di continuare", unit: "count", semantic: "conversations" },
    { key: "willing_to_pay", label: "Si sono dichiarate disposte a pagare", unit: "count", semantic: "commitments" },
  ],
};

const UNIVERSAL_FALLBACK_DEFS: MetricDef[] = [
  { key: "reached", label: "Persone raggiunte", unit: "count", semantic: "reached" },
  { key: "responses", label: "Risposte o interazioni", unit: "count", semantic: "responses" },
  { key: "actions", label: "Azioni concrete osservate", unit: "count", semantic: "qualified" },
  { key: "commitments", label: "Micro-impegni ottenuti", unit: "count", semantic: "commitments" },
  { key: "primary_result", label: "Risultato principale", unit: "count", semantic: "primary_custom", is_primary: true },
];

interface Step3MetricLite {
  id?: string;
  label?: string;
  checked?: boolean;
}

/** Builds the Step 4 metric scaffold (no values), driven by experiment +
 *  optional Step 3 personalized list. Always ensures exactly one is_primary. */
export function buildStep4MetricDefinitions(
  experimentId: string | undefined | null,
  step3Metrics?: Step3MetricLite[] | null,
): MetricResult[] {
  const norm = normalizeExperimentId(experimentId ?? "");
  const presetDefs = STEP4_METRIC_DEFS[norm] ?? UNIVERSAL_FALLBACK_DEFS;

  // If Step 3 has user-customized metric labels, prefer those for the labels
  // but keep preset semantics by best-effort label match. Step 3-only labels
  // (not in preset) become source="step3".
  const presetByLabel = new Map<string, MetricDef>();
  for (const d of presetDefs) presetByLabel.set(d.label.toLowerCase(), d);

  let results: MetricResult[];
  if (Array.isArray(step3Metrics) && step3Metrics.length > 0) {
    results = step3Metrics.map((m, i) => {
      const label = (m.label ?? "").trim() || `Metrica ${i + 1}`;
      const matched = presetByLabel.get(label.toLowerCase());
      if (matched) {
        return {
          id: `step3_${matched.key}`,
          label,
          value: null,
          unit: matched.unit,
          source: "step3" as const,
          available_from_step3: !!m.checked,
          is_primary: !!matched.is_primary,
          semantic: matched.semantic,
        };
      }
      return {
        id: `step3_custom_${i}`,
        label,
        value: null,
        unit: "count" as const,
        source: "step3" as const,
        available_from_step3: !!m.checked,
        semantic: "other" as const,
      };
    });
  } else {
    results = presetDefs.map((d) => ({
      id: `${norm}_${d.key}`,
      label: d.label,
      value: null,
      unit: d.unit,
      source: norm === "custom" ? ("fallback" as const) : ("preset" as const),
      is_primary: !!d.is_primary,
      semantic: d.semantic,
    }));
  }

  // Guarantee exactly one primary. If none, pick the last semantic="commitments"
  // or "qualified" or the last item.
  if (!results.some((r) => r.is_primary)) {
    const findLast = (pred: (r: MetricResult) => boolean): MetricResult | undefined => {
      for (let i = results.length - 1; i >= 0; i--) if (pred(results[i])) return results[i];
      return undefined;
    };
    const pick =
      findLast((r) => r.semantic === "commitments") ??
      findLast((r) => r.semantic === "qualified") ??
      results[results.length - 1];
    if (pick) pick.is_primary = true;
  }
  return results;
}

// ----- Legacy → MetricResult mapping (read-only, non-destructive) ---------

/** When a legacy submission has the flat `metrics` blob (contacted_count, …)
 *  and no `metric_results`, fill values in the new scaffold by semantic. */
export function mapLegacyToMetricResults(
  legacyMetrics: Record<string, unknown> | null | undefined,
  defs: MetricResult[],
): MetricResult[] {
  if (!legacyMetrics) return defs;
  const map: Partial<Record<MetricSemantic, number>> = {};
  const num = (v: unknown): number | null =>
    typeof v === "number" && Number.isFinite(v)
      ? v
      : typeof v === "string" && v.trim() !== "" && !isNaN(Number(v))
        ? Number(v)
        : null;
  const setSem = (sem: MetricSemantic, v: unknown) => {
    const n = num(v);
    if (n != null && map[sem] == null) map[sem] = n;
  };
  setSem("reached", legacyMetrics.contacted_count);
  setSem("responses", legacyMetrics.response_count);
  setSem("conversations", legacyMetrics.conversation_count);
  setSem("commitments", legacyMetrics.micro_commitment_count);
  return defs.map((d) => {
    if (d.value != null) return d;
    const v = d.semantic ? map[d.semantic] : undefined;
    return v != null ? { ...d, value: v } : d;
  });
}

// ----- Derived metrics ----------------------------------------------------

export interface DerivedMetric {
  id: string;
  label: string;
  value: number;
  unit: "percentage" | "ratio";
}

/** Conversion-rate / CTR computed from raw metric_results.
 *  Never divides by zero. */
export function computeDerivedMetrics(
  results: MetricResult[],
  experimentId: string | undefined | null,
): DerivedMetric[] {
  const norm = normalizeExperimentId(experimentId ?? "");
  const bySem = new Map<MetricSemantic, MetricResult>();
  for (const r of results) if (r.semantic) bySem.set(r.semantic, r);
  const out: DerivedMetric[] = [];
  const reached = bySem.get("reached")?.value ?? null;
  const responses = bySem.get("responses")?.value ?? null;
  const commitments = bySem.get("commitments")?.value ?? null;

  const pct = (num: number, den: number) =>
    Math.round((num / den) * 1000) / 10;

  if (norm === "landing_waitlist" && reached && reached > 0) {
    if (commitments != null)
      out.push({
        id: "derived_signup_rate",
        label: "Conversion rate (iscrizioni / visite)",
        value: pct(commitments, reached),
        unit: "percentage",
      });
    if (responses != null)
      out.push({
        id: "derived_cta_ctr",
        label: "Click-through rate (click / visite)",
        value: pct(responses, reached),
        unit: "percentage",
      });
  }
  if ((norm === "fake_door" || norm === "smoke_test") && reached && reached > 0) {
    if (responses != null)
      out.push({
        id: "derived_ctr",
        label: "CTR (click / impression)",
        value: pct(responses, reached),
        unit: "percentage",
      });
    if (commitments != null)
      out.push({
        id: "derived_conv",
        label: "Conversion rate (contatti / impression)",
        value: pct(commitments, reached),
        unit: "percentage",
      });
  }
  return out;
}

// ----- Threshold parsing --------------------------------------------------

/** Deterministic, conservative threshold parser. Returns `high` confidence
 *  only when both an operator AND a number are unambiguous. Never invents. */
export function parseThreshold(
  raw: string | null | undefined,
  metricDefs: MetricResult[] = [],
): ThresholdAssessment {
  const source_text = (raw ?? "").toString();
  const base: ThresholdAssessment = {
    source_text,
    mapped_metric_id: null,
    operator: null,
    target_value: null,
    denominator_metric_id: null,
    denominator_target: null,
    timeframe_text: null,
    parse_confidence: "low",
    manually_confirmed: false,
  };
  const t = source_text.trim().toLowerCase();
  if (!t) return base;

  // Operator detection
  let operator: ThresholdAssessment["operator"] = null;
  if (/(≥|>=|almeno|minimo|min\.|at least)/.test(t)) operator = "gte";
  else if (/(>|più di|piu di|oltre)/.test(t)) operator = "gt";
  else if (/(≤|<=|massimo|al massimo|max\.|al più|al piu)/.test(t)) operator = "lte";
  else if (/(<|meno di)/.test(t)) operator = "lt";

  // Percentage form: "≥ 3%", "almeno 5%"
  const pct = t.match(/(\d+(?:[\.,]\d+)?)\s*%/);
  // "N su M" form
  const ratio = t.match(/(\d+)\s*(?:su|\/|of|on)\s*(\d+)/);
  // Plain integer
  const plain = t.match(/(\d+(?:[\.,]\d+)?)/);

  // Timeframe
  const tf = t.match(/entro\s+\d+\s+(giorn[io]|settiman[ae]|or[ae])/);
  base.timeframe_text = tf ? tf[0] : null;

  // Try ratio "N su M" first — it gives both numerator + denominator
  if (ratio) {
    const num = parseInt(ratio[1], 10);
    const den = parseInt(ratio[2], 10);
    if (Number.isFinite(num) && Number.isFinite(den) && den > 0) {
      base.operator = operator ?? "gte";
      base.target_value = num;
      base.denominator_target = den;
      // Map metric only if the metric defs contain something unambiguous.
      const numericMetric = mapTextToMetric(t, metricDefs);
      const denomMetric = mapDenominatorToMetric(metricDefs);
      base.mapped_metric_id = numericMetric;
      base.denominator_metric_id = denomMetric;
      base.parse_confidence = numericMetric ? "high" : "low";
      return base;
    }
  }

  if (pct) {
    const v = parseFloat(pct[1].replace(",", "."));
    if (Number.isFinite(v)) {
      base.operator = operator ?? "gte";
      base.target_value = v;
      // Percentage targets need a derived metric mapping — uncertain by default.
      base.parse_confidence = "low";
      return base;
    }
  }

  if (plain && operator) {
    const v = parseFloat(plain[1].replace(",", "."));
    if (Number.isFinite(v)) {
      base.operator = operator;
      base.target_value = v;
      const m = mapTextToMetric(t, metricDefs);
      base.mapped_metric_id = m;
      base.parse_confidence = m ? "high" : "low";
      return base;
    }
  }
  return base;
}

function mapTextToMetric(
  text: string,
  defs: MetricResult[],
): string | null {
  const t = text.toLowerCase();
  // Semantic keyword → match the def with that semantic
  const semHits: Array<{ sem: MetricSemantic; rx: RegExp }> = [
    { sem: "commitments", rx: /(iscriz|email|signup|sign-up|preorder|pre-order|pagar)/ },
    { sem: "qualified", rx: /(qualific|confer|completat|problema|demo)/ },
    { sem: "conversations", rx: /(intervist|conversaz|call|accettat)/ },
    { sem: "responses", rx: /(click|rispost|reaz|dm)/ },
    { sem: "reached", rx: /(visit|impres|raggiunt|contatt|invit)/ },
  ];
  const matchedSems = semHits.filter((h) => h.rx.test(t)).map((h) => h.sem);
  if (matchedSems.length !== 1) return null; // ambiguous → no auto-map
  const sem = matchedSems[0];
  const cand = defs.filter((d) => d.semantic === sem);
  if (cand.length !== 1) return null;
  return cand[0].id;
}

function mapDenominatorToMetric(defs: MetricResult[]): string | null {
  const reached = defs.filter((d) => d.semantic === "reached");
  return reached.length === 1 ? reached[0].id : null;
}

// ----- Evidence quality ---------------------------------------------------

export function evaluateEvidenceQuality(args: {
  metricResults: MetricResult[];
  threshold: ThresholdAssessment;
  qualitativeEvidence: QualitativeEvidence[];
  typedLinks: TypedEvidenceLink[];
  sampleCollected: number | null;
  sampleTarget: number | null;
}): EvidenceQualityAssessment {
  const primary = args.metricResults.find((m) => m.is_primary);
  const primary_metric_present = !!primary && primary.value != null;
  const qualitative_evidence_present = args.qualitativeEvidence.some(
    (q) => (q.quote ?? "").trim().length > 0,
  );
  const supporting_link_present = args.typedLinks.some(
    (l) => (l.url ?? "").trim().length > 0,
  );
  const threshold_comparable =
    args.threshold.target_value != null &&
    args.threshold.mapped_metric_id != null &&
    (args.threshold.parse_confidence === "high" ||
      args.threshold.manually_confirmed);
  const sample_sufficient =
    args.sampleTarget == null
      ? null
      : args.sampleCollected != null && args.sampleCollected >= args.sampleTarget;

  let level: EvidenceQualityAssessment["level"] = "weak";
  if (
    primary_metric_present &&
    threshold_comparable &&
    (qualitative_evidence_present || supporting_link_present)
  ) {
    level = "sufficient";
  }
  const qualitativeCount = args.qualitativeEvidence.filter(
    (q) => (q.quote ?? "").trim().length > 0,
  ).length;
  if (
    primary_metric_present &&
    threshold_comparable &&
    qualitativeCount >= 2 &&
    supporting_link_present &&
    (sample_sufficient === true ||
      (args.sampleTarget != null &&
        args.sampleCollected != null &&
        args.sampleCollected >= 0.8 * args.sampleTarget))
  ) {
    level = "strong";
  }
  return {
    level,
    checks: {
      primary_metric_present,
      sample_sufficient,
      threshold_comparable,
      qualitative_evidence_present,
      supporting_link_present,
    },
  };
}

// ----- "Cosa manca" -------------------------------------------------------

export function computeStep4Gaps(args: {
  metricResults: MetricResult[];
  threshold: ThresholdAssessment;
  qualitativeEvidence: QualitativeEvidence[];
  typedLinks: TypedEvidenceLink[];
}): string[] {
  const out: string[] = [];
  const primary = args.metricResults.find((m) => m.is_primary);
  if (!primary || primary.value == null) {
    out.push(`Valore della metrica principale: ${primary?.label ?? "risultato principale"}`);
  }
  if (
    args.threshold.source_text.trim() &&
    args.threshold.parse_confidence === "low" &&
    !args.threshold.manually_confirmed
  ) {
    out.push("Conferma a quale metrica si riferisce la soglia definita nello Step 2");
  }
  const hasQual = args.qualitativeEvidence.some((q) => (q.quote ?? "").trim());
  const hasLink = args.typedLinks.some((l) => (l.url ?? "").trim());
  if (!hasQual && !hasLink) {
    out.push("Almeno una prova concreta (citazione, screenshot, link)");
  }
  return out.slice(0, 3);
}

// ----- Legacy mirrors (Step 5 compatibility) ------------------------------

/** Build the legacy flat `metrics` object expected by the current Step 5
 *  resultsLine renderer. Never returns NaN. */
export function buildLegacyMetricsMirror(
  results: MetricResult[],
  existing?: Record<string, unknown> | null,
): Record<string, number | null> {
  const out: Record<string, number | null> = { ...(existing as any) };
  const findVal = (sem: MetricSemantic): number | null => {
    const m = results.find((r) => r.semantic === sem && r.value != null);
    return m?.value ?? null;
  };
  const reached = findVal("reached");
  const responses = findVal("responses");
  const conversations = findVal("conversations");
  const commitments = findVal("commitments");
  if (reached != null) out.contacted_count = reached;
  if (responses != null) out.response_count = responses;
  if (conversations != null) out.conversation_count = conversations;
  if (commitments != null) out.micro_commitment_count = commitments;
  return out;
}

/** Mirror typed links into the legacy `evidence_links: string[]` column,
 *  deduped, preserving any existing entries not covered by typed links. */
export function buildLegacyEvidenceLinksMirror(
  typed: TypedEvidenceLink[],
  existing: string[] = [],
): string[] {
  const urls = new Set<string>();
  const out: string[] = [];
  for (const t of typed) {
    const u = (t.url ?? "").trim();
    if (!u || urls.has(u)) continue;
    urls.add(u);
    out.push(u);
  }
  for (const e of existing) {
    const u = (e ?? "").trim();
    if (!u || urls.has(u)) continue;
    urls.add(u);
    out.push(u);
  }
  return out;
}

/** Mirror qualitative evidence quotes into the legacy `real_quotes` text
 *  field, so any consumer that still reads the textarea keeps working. */
export function buildLegacyRealQuotesMirror(
  evidence: QualitativeEvidence[],
  existing?: string | null,
): string {
  const lines = evidence
    .map((e) => (e.quote ?? "").trim())
    .filter(Boolean)
    .map((q, i) => {
      const ev = evidence[i];
      const src = (ev.source ?? "").trim();
      return src ? `"${q}" — ${src}` : `"${q}"`;
    });
  if (lines.length > 0) return lines.join("\n");
  return (existing ?? "").toString();
}



// ===========================================================================
// Step 5 — Decision helpers (deterministic, no AI)
// ===========================================================================

export type Step5Verdict = "continue" | "iterate" | "stop";
export type Step5DecisionStatus = "draft" | "confirmed";

export type Step5ChangeDimension =
  | "target"
  | "channel"
  | "message"
  | "offer"
  | "pricing"
  | "solution"
  | "other";

export type Step5StopDisposition = "pivot" | "pause" | "archive";

export interface SuggestedVerdictResult {
  status: "available" | "insufficient_evidence";
  verdict: Step5Verdict | null;
  confidence: "medium" | "high" | null;
  reasons: string[];
}

/** Map legacy IT verdict tokens to the new canonical EN values. */
export function mapLegacyVerdict(v: unknown): Step5Verdict | null {
  if (typeof v !== "string") return null;
  const k = v.trim().toLowerCase();
  if (!k) return null;
  if (["continue", "continua", "go", "proceed"].includes(k)) return "continue";
  if (["iterate", "itera", "iter", "pivot_minor"].includes(k)) return "iterate";
  if (["stop", "fermati", "kill", "abandon"].includes(k)) return "stop";
  return null;
}

/** Convert canonical verdict back to the legacy IT token used by older UIs. */
export function verdictToLegacy(v: Step5Verdict | null): string | null {
  if (v === "continue") return "continua";
  if (v === "iterate") return "itera";
  if (v === "stop") return "fermati";
  return null;
}

/** Deterministic suggested verdict.
 *  Never returns "stop" when the sample is clearly insufficient.
 *  Never returns "available" when evidence is weak without a primary value. */
export function deriveSuggestedVerdict(args: {
  primaryValue: number | null;
  thresholdValue: number | null;
  thresholdOperator: ThresholdAssessment["operator"];
  thresholdComparable: boolean;
  sampleCollected: number | null;
  sampleTarget: number | null;
  evidenceLevel: EvidenceQualityAssessment["level"];
}): SuggestedVerdictResult {
  const {
    primaryValue,
    thresholdValue,
    thresholdOperator,
    thresholdComparable,
    sampleCollected,
    sampleTarget,
    evidenceLevel,
  } = args;

  const reasons: string[] = [];
  const insufficient = (extra: string[] = []): SuggestedVerdictResult => ({
    status: "insufficient_evidence",
    verdict: null,
    confidence: null,
    reasons: extra.slice(0, 3),
  });

  if (primaryValue == null) {
    return insufficient(["Manca il valore della metrica principale nello Step 4."]);
  }
  if (!thresholdComparable || thresholdValue == null) {
    return insufficient(["La soglia dello Step 2 non è confrontabile con una metrica."]);
  }
  if (evidenceLevel === "weak") {
    return insufficient([
      "La qualità complessiva delle evidenze è ancora debole.",
      "Aggiungi citazioni, prove o conferma la soglia per ottenere un suggerimento affidabile.",
    ]);
  }

  // Threshold comparison
  const op = thresholdOperator ?? "gte";
  const thresholdMet =
    op === "gte"
      ? primaryValue >= thresholdValue
      : op === "gt"
        ? primaryValue > thresholdValue
        : op === "lte"
          ? primaryValue <= thresholdValue
          : op === "lt"
            ? primaryValue < thresholdValue
            : op === "equal"
              ? primaryValue === thresholdValue
              : primaryValue >= thresholdValue;

  // For lte/lt we don't compute a meaningful "ratio"; treat as binary.
  const isUpperBound = op === "lte" || op === "lt";
  const achievementRatio =
    isUpperBound || thresholdValue === 0
      ? thresholdMet
        ? 1
        : 0
      : primaryValue / thresholdValue;

  // Sample status
  const sampleClearlyBelow =
    sampleTarget != null &&
    sampleCollected != null &&
    sampleCollected < 0.5 * sampleTarget;
  const sampleSufficientOrClose =
    sampleTarget == null ||
    (sampleCollected != null && sampleCollected >= 0.8 * sampleTarget);

  // Decision
  if (thresholdMet && !sampleClearlyBelow) {
    reasons.push("Soglia raggiunta sulla metrica principale.");
    if (evidenceLevel === "strong") reasons.push("Le evidenze qualitative supportano il risultato.");
    if (sampleTarget && sampleCollected != null && sampleCollected >= sampleTarget)
      reasons.push("Campione minimo raggiunto.");
    return {
      status: "available",
      verdict: "continue",
      confidence: evidenceLevel === "strong" ? "high" : "medium",
      reasons: reasons.slice(0, 3),
    };
  }

  if (achievementRatio < 0.5 && sampleSufficientOrClose) {
    reasons.push("Risultato molto distante dalla soglia minima.");
    if (sampleTarget && sampleCollected != null && sampleCollected >= sampleTarget)
      reasons.push("Campione sufficiente: i numeri sono rappresentativi.");
    if (evidenceLevel === "strong") reasons.push("Le evidenze qualitative confermano la difficoltà.");
    return {
      status: "available",
      verdict: "stop",
      confidence: evidenceLevel === "strong" ? "high" : "medium",
      reasons: reasons.slice(0, 3),
    };
  }

  // Iterate fallback
  if (sampleClearlyBelow) reasons.push("Campione raccolto sotto il minimo previsto.");
  else if (!thresholdMet) reasons.push("Risultato vicino o sotto la soglia, ma con segnali utili.");
  else reasons.push("Evidenze sufficienti ma non conclusive: una nuova iterazione può chiarire.");
  if (evidenceLevel === "sufficient")
    reasons.push("La qualità delle evidenze è sufficiente per imparare, non per concludere.");
  return {
    status: "available",
    verdict: "iterate",
    confidence: "medium",
    reasons: reasons.slice(0, 3),
  };
}

// ----- Legacy mirrors per Step 5 ------------------------------------------

export interface GuidedRationaleLike {
  evidence_supporting?: string;
  evidence_against?: string;
  bias_or_uncertainty?: string;
}

export interface LearningCaptureLike {
  market_learning?: string;
  process_learning?: string;
}

/** Builds the legacy single-textarea `rationale` string from the new
 *  guided rationale + learning capture, so older readers keep working. */
export function buildLegacyRationale(
  r: GuidedRationaleLike | null | undefined,
  l: LearningCaptureLike | null | undefined,
): string {
  const parts: string[] = [];
  const add = (label: string, val: string | undefined) => {
    const v = (val ?? "").trim();
    if (v) parts.push(`${label}: ${v}`);
  };
  add("A favore", r?.evidence_supporting);
  add("Contro", r?.evidence_against);
  add("Bias/incertezza", r?.bias_or_uncertainty);
  add("Imparato sul mercato", l?.market_learning);
  add("Imparato sul processo", l?.process_learning);
  return parts.join("\n\n");
}

/** Flattens the structured next_step into the legacy free-text field. */
export function buildLegacyNextStep(next: unknown): string {
  if (!next || typeof next !== "object") return "";
  const n = next as Record<string, unknown>;
  const out: string[] = [];
  const add = (label: string, key: string) => {
    const v = typeof n[key] === "string" ? (n[key] as string).trim() : "";
    if (v) out.push(`${label}: ${v}`);
  };
  add("Prossima ipotesi", "next_hypothesis");
  add("Prossimo esperimento", "next_experiment");
  add("Metrica", "next_metric");
  add("Cosa cambio", "change_dimension");
  add("Modifica concreta", "change_description");
  add("Perché", "why_this_change");
  add("Cosa preservo", "what_to_preserve");
  add("Cosa smetto", "what_to_stop");
  add("Disposizione", "disposition");
  add("Possibile pivot", "possible_pivot");
  add("Entro", "target_date");
  return out.join("\n");
}

// ----- Markdown summary ---------------------------------------------------

export interface Step5SummaryInput {
  hypothesis: string;
  experimentLabel: string;
  target: string;
  channel: string;
  thresholdText: string;
  primaryLabel: string;
  primaryValue: number | null;
  thresholdAssessmentMet: boolean | null;
  evidenceLevel: EvidenceQualityAssessment["level"];
  sampleCollected: number | null;
  sampleTarget: number | null;
  verdict: Step5Verdict | null;
  suggested: SuggestedVerdictResult;
  rationale: GuidedRationaleLike;
  learning: LearningCaptureLike;
  nextStep: Record<string, unknown> | null;
  startedAt: string | null;
  confirmedAt: string | null;
}

export function buildStep5DecisionSummary(s: Step5SummaryInput): string {
  const fmtDate = (iso: string | null) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleDateString("it-IT", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
    } catch {
      return null;
    }
  };
  const verdictLabel = (v: Step5Verdict | null) =>
    v === "continue" ? "Continua" : v === "iterate" ? "Itera" : v === "stop" ? "Fermati" : "—";
  const qualityLabel = (l: EvidenceQualityAssessment["level"]) =>
    l === "strong" ? "Solide" : l === "sufficient" ? "Sufficienti" : "Deboli";

  const lines: string[] = [];
  lines.push(`# Riepilogo Sprint`);
  if (s.hypothesis) lines.push(`\n**Ipotesi testata:** ${s.hypothesis}`);
  if (s.experimentLabel) lines.push(`**Esperimento:** ${s.experimentLabel}`);
  if (s.target) lines.push(`**Target:** ${s.target}`);
  if (s.channel) lines.push(`**Canale:** ${s.channel}`);

  lines.push(`\n## Risultato vs soglia`);
  lines.push(
    `- Risultato principale (${s.primaryLabel || "metrica principale"}): ${s.primaryValue ?? "—"}`,
  );
  lines.push(`- Soglia (Step 2): ${s.thresholdText || "non definita"}`);
  if (s.sampleTarget != null) {
    lines.push(`- Campione: ${s.sampleCollected ?? "—"} su ${s.sampleTarget} previsti`);
  }
  lines.push(`- Qualità evidenze: ${qualityLabel(s.evidenceLevel)}`);

  lines.push(`\n## Verdetto`);
  lines.push(`- Decisione: **${verdictLabel(s.verdict)}**`);
  if (s.suggested.status === "available") {
    lines.push(`- Suggerito dai dati: ${verdictLabel(s.suggested.verdict)}`);
  } else {
    lines.push(`- Suggerito dai dati: evidenze insufficienti`);
  }

  const rat: string[] = [];
  if (s.rationale.evidence_supporting?.trim())
    rat.push(`- A favore: ${s.rationale.evidence_supporting!.trim()}`);
  if (s.rationale.evidence_against?.trim())
    rat.push(`- Contro: ${s.rationale.evidence_against!.trim()}`);
  if (s.rationale.bias_or_uncertainty?.trim())
    rat.push(`- Bias / incertezza: ${s.rationale.bias_or_uncertainty!.trim()}`);
  if (rat.length) {
    lines.push(`\n## Razionale`);
    lines.push(rat.join("\n"));
  }

  const learn: string[] = [];
  if (s.learning.market_learning?.trim())
    learn.push(`- Mercato/target: ${s.learning.market_learning!.trim()}`);
  if (s.learning.process_learning?.trim())
    learn.push(`- Processo di validazione: ${s.learning.process_learning!.trim()}`);
  if (learn.length) {
    lines.push(`\n## Cosa ho imparato`);
    lines.push(learn.join("\n"));
  }

  if (s.nextStep) {
    const ns = s.nextStep as Record<string, string>;
    const items: string[] = [];
    const add = (label: string, key: string) => {
      const v = (ns[key] ?? "").toString().trim();
      if (v) items.push(`- ${label}: ${v}`);
    };
    if (s.verdict === "continue") {
      add("Prossima ipotesi", "next_hypothesis");
      add("Prossimo esperimento", "next_experiment");
      add("Metrica", "next_metric");
      add("Entro", "target_date");
    } else if (s.verdict === "iterate") {
      add("Cosa cambio", "change_dimension");
      add("Modifica concreta", "change_description");
      add("Perché", "why_this_change");
      add("Metrica da confrontare", "next_metric");
      add("Entro", "target_date");
    } else if (s.verdict === "stop") {
      add("Cosa preservo", "what_to_preserve");
      add("Cosa smetto", "what_to_stop");
      add("Disposizione", "disposition");
      add("Possibile pivot", "possible_pivot");
    }
    if (items.length) {
      lines.push(`\n## Prossimo passo`);
      lines.push(items.join("\n"));
    }
  }

  const started = fmtDate(s.startedAt);
  const closed = fmtDate(s.confirmedAt);
  if (started || closed) {
    lines.push(`\n## Date`);
    if (started) lines.push(`- Sprint avviato: ${started}`);
    if (closed) lines.push(`- Sprint chiuso: ${closed}`);
    if (started && closed && s.startedAt && s.confirmedAt) {
      const days = Math.max(
        0,
        Math.round(
          (new Date(s.confirmedAt).getTime() - new Date(s.startedAt).getTime()) /
            (1000 * 60 * 60 * 24),
        ),
      );
      lines.push(`- Durata effettiva: ${days} giorni`);
    }
  }

  return lines.join("\n");
}
